/* eslint-disable lingui/no-unlocalized-strings */

import { Briefcase, CloudArrowUp, FileText, MagicWand, UploadSimple } from "@phosphor-icons/react";
import { defaultResumeData } from "@reactive-resume/schema";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import { toast } from "@/client/hooks/use-toast";
// Review of the AI's suggestions happens in the builder, not on this page. These two seed the same
// store the LMS handoff uses, so both entry points share one suggestion UI.
import { buildCoachPlan } from "@/client/pages/coach/optimize/build-coach-plan";
// The scan animation, shared with /coach/optimize rather than re-implemented here. See ResumeScanLoader.
import { CoachResumeScan } from "@/client/pages/coach/optimize/coach-resume-scan";
import {
  buildDashboardCoachSession,
  RESUME_FIT_WEIGHTS,
  startCoachReviewInBuilder,
} from "@/client/pages/coach/optimize/start-in-builder";
import { fetchUploadedFileText } from "@/client/services/openai/resume-optimization";
import { useResumes, useUpdateResume } from "@/client/services/resume";
import { useImportResume } from "@/client/services/resume/import";
import { usePrintResume } from "@/client/services/resume/print";
import { uploadDocument } from "@/client/services/storage";

import { OptimizationSuggestion, shouldShowOptimizationEntry } from "./review";
import { buildTemporaryUploadedResume } from "./uploaded-resume";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const stringifyResume = (resume: unknown) =>
  JSON.stringify(
    resume,
    (_key, value) => {
      if (value instanceof Date) return value.toISOString();
      return value;
    },
    2,
  );

const FieldShell = ({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-lg border border-border bg-secondary/20 p-4 shadow-sm">
    <div className="mb-4 flex items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-xs opacity-70">{subtitle}</p>
      </div>
    </div>
    {children}
  </section>
);

/**
 * The wait screen while the model works, over this page's own intake form.
 *
 * ⚠️ IT RENDERS THE REAL RÉSUMÉ, via the same `CoachResumeScan` the `/coach/optimize` handoff uses.
 * This used to be a bespoke mock: twelve grey divs shaped roughly like a document, with its own
 * `@keyframes resume-scan` sweep and its own emerald palette — a second implementation of an
 * animation that already existed, and one that showed a generic page rather than the student's own.
 * Two costs, and the second is the real one:
 *
 *   - the two entry points to one feature animated differently, on different colours, because nothing
 *     tied them together;
 *   - a mock cannot confirm anything. The student has just picked a résumé from a dropdown or dragged
 *     in a file, and the next thing they see is the builder. Showing the ACTUAL document being read is
 *     the only point in the flow where picking the wrong one is cheap to notice.
 *
 * `resumeData` is null for the first moment (and for an upload, until its text has been extracted),
 * which `CoachResumeScan` already handles with a skeleton in the same paper shape — so the card does
 * not resize when the document arrives.
 */
export const ResumeScanLoader = ({ resumeData }: { resumeData?: unknown }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
    <div
      role="status"
      aria-label="Scanning your resume"
      className="relative flex w-full max-w-sm flex-col items-center overflow-hidden rounded-lg border border-emerald-400/20 bg-[#050705] px-6 py-8 text-center shadow-[0_0_60px_rgba(16,185,129,0.18)]"
    >
      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-24 -translate-y-1/2 bg-emerald-400/10 blur-3xl" />

      {/* Fixed width, not `w-full`: CoachResumeScan is aspect-locked to the page size and sizes itself
          from its container's width, so this is what decides how tall the paper is inside the modal. */}
      <div className="relative w-[232px]">
        <CoachResumeScan resumeData={resumeData ?? null} scanning />
      </div>

      <p className="relative mt-6 text-sm font-semibold text-white">Scanning your resume...</p>
      <p className="relative mt-1 text-xs text-zinc-400">Finding matches for the target job</p>
    </div>
  </div>
);
// The in-page review workspace that used to live here is gone: AiReviewWorkspace, its two résumé
// preview frames, ScoreCircle and ReviewOption — roughly 400 lines re-implementing the builder at
// lower fidelity. Optimizing now seeds the coach store and navigates to /builder/:id, which reviews
// the suggestions in place. See pages/coach/optimize/start-in-builder.ts.

export const AiOptimizationPage = () => {
  const navigate = useNavigate();
  const { resumes, loading: loadingResumes } = useResumes();
  const { importResume, loading: creatingResume } = useImportResume();
  // `useUpdateResume` / `usePrintResume` are no longer called from here — saving and exporting are the
  // builder's job now. Only their loading flags are still read, to keep the Optimize button disabled
  // while a résumé is being written.
  const { loading: savingResume } = useUpdateResume();
  const { loading: printingResume } = usePrintResume();
  const [jobDescription, setJobDescription] = useState("");
  const [jobFile, setJobFile] = useState<File | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [selectedResumeId, setSelectedResumeId] = useState("none");
  const [suggestions, setSuggestions] = useState<OptimizationSuggestion[]>([]);
  /**
   * The document the scan animation shows.
   *
   * Separate state rather than derived from `selectedResume`, because the other input is an UPLOADED
   * FILE, whose résumé data only exists once its text has been extracted partway through `onOptimize`.
   * Set at whichever point the document becomes known, so the preview is up for the seconds the
   * student actually spends waiting on the model rather than appearing as the screen navigates away.
   */
  const [previewData, setPreviewData] = useState<unknown>(null);
  // `suggestionDrafts`, `acceptedSuggestionIds`, `reviewResumeData`, `savedResume` and `showSuccess`
  // are all gone with the review workspace. The coach store and the builder hold that state now, which
  // is what stops this page and the builder keeping two divergent copies of the student's edits.
  const [loading, setLoading] = useState(false);

  const selectedResume = useMemo(
    () => resumes?.find((resume) => resume.id === selectedResumeId),
    [resumes, selectedResumeId],
  );

  const hasResumeInput = !!resumeFile || !!selectedResume;
  const hasJobInput = jobDescription.trim().length > 0 || !!jobFile;

  // Matters now that Optimize imports a résumé and navigates: a second click while the first import is
  // in flight would create a duplicate résumé and open the builder on the wrong one.
  const isPreparingResume = savingResume || creatingResume || printingResume;

  const canOptimize = hasJobInput && hasResumeInput && !loading && !isPreparingResume;
  const showOptimizationEntry = shouldShowOptimizationEntry(suggestions);

  // `acceptedSuggestions` / `draftResumeData` / `canExportResume` are gone with the review workspace.
  // Accepts are applied to the resume store by the builder, so there is no second draft to maintain
  // here — and maintaining one was how the page's preview and the builder could disagree.

  const validateFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error("Maximum file size is 10 MB.");
    }
  };

  const handleJobFileChange = (file?: File) => {
    if (!file) return;
    try {
      validateFile(file);
      setJobFile(file);
    } catch (error) {
      toast({
        variant: "error",
        title: error instanceof Error ? error.message : "Invalid file.",
      });
    }
  };

  const handleResumeFileChange = (file?: File) => {
    if (!file) return;
    try {
      validateFile(file);
      setResumeFile(file);
      setSelectedResumeId("none");
    } catch (error) {
      toast({
        variant: "error",
        title: error instanceof Error ? error.message : "Invalid file.",
      });
    }
  };

  const onOptimize = async () => {
    try {
      setLoading(true);
      setSuggestions([]);
      // A selected résumé is already in hand, so the preview can be correct from the first frame of
      // the overlay. An upload has to wait for its text extraction below.
      setPreviewData(selectedResume?.data ?? null);

      const [jobFileUpload, resumeFileUpload] = await Promise.all([
        jobFile ? uploadDocument(jobFile) : Promise.resolve(null),
        resumeFile ? uploadDocument(resumeFile) : Promise.resolve(null),
      ]);
      const jobFileUrl = jobFileUpload?.url ?? "";
      const resumeFileUrl = resumeFileUpload?.url ?? "";
      const finalJobDescription = jobDescription;
      const resumeExtractedText =
        resumeFile && resumeFileUrl ? await fetchUploadedFileText(resumeFileUrl, "Resume") : "";
      const uploadedResumeData =
        resumeFile && !selectedResume
          ? buildTemporaryUploadedResume(resumeFile, resumeExtractedText)
          : null;

      // The upload's document now exists, so the scan can stop showing a skeleton and show it.
      if (uploadedResumeData) setPreviewData(uploadedResumeData);

      const resumeContent = selectedResume
        ? stringifyResume(selectedResume.data)
        : resumeExtractedText || stringifyResume(uploadedResumeData ?? defaultResumeData);

      if (!finalJobDescription.trim() && !jobFileUrl) {
        throw new Error("Add or upload a job description first.");
      }

      if (!resumeContent.trim() && !resumeFileUrl) {
        throw new Error("Upload a resume or select an existing resume first.");
      }

      // The resume has to be persisted before planning: buildCoachPlan reads stored resume data, and
      // the builder loads by id. A selected resume already is one; an upload becomes one here.
      const reviewResume = selectedResume
        ? selectedResume
        : await importResume({ data: uploadedResumeData ?? defaultResumeData });

      if (!reviewResume?.id) throw new Error("Could not prepare a resume to review.");

      // buildCoachPlan, NOT the older optimizeResumeForJob. This page used to call its own prompt,
      // which produced visibly different results from the student flow on the very same screen: plain
      // rewrites with no attributed gains, no "Skills to add" cards, and no way to retitle a skill,
      // because it built its editable fields with the base helper that never exposes a skill's `name`.
      // It also skipped verification and no-op filtering, so it could hand the builder a suggestion
      // aimed at a fieldPath the resume does not have.
      //
      // No baseline and no skill lists are passed: there is no job record here, so the model
      // self-assesses and reads the required skills out of the job description text itself.
      const plan = await buildCoachPlan({
        jobDescription: finalJobDescription.trim() || resumeContent,
        resumeData: reviewResume.data,
        baseline: { roleDomainFit: 0, skillOverlap: 0 },
        // The same weights `buildDashboardCoachSession` scores this flow with, so the prompt's
        // account of what a sub-score point is worth matches the donut the student then sees.
        weights: RESUME_FIT_WEIGHTS,
      });

      // Review happens in the BUILDER, not on this page. It used to populate an AiReviewWorkspace
      // embedded below: a second resume preview with its own typography and layout panels and its own
      // accept/reject list, i.e. the builder rebuilt at lower fidelity, where a student could not see
      // the rest of their resume while deciding on a rewrite. Both entry points now open the real
      // builder, so there is one implementation of this UI.
      startCoachReviewInBuilder({
        session: buildDashboardCoachSession({
          // Named from the uploaded file when there is no job record to name it from, so the builder's
          // header still says what is being optimized against.
          jobTitle: jobFile?.name.replace(/\.[^.]+$/, "") || "Uploaded job description",
          // The model's own read of the resume becomes the baseline, so this flow shows the same score
          // donut and the same per-suggestion badges as the student flow.
          assessed: plan.assessed,
        }),
        resumeId: reviewResume.id,
        plan,
      });

      navigate(`/builder/${reviewResume.id}`);
    } catch (error) {
      toast({
        variant: "error",
        title: error instanceof Error ? error.message : "Failed to optimize resume.",
      });
    } finally {
      setLoading(false);
    }
  };

  // The accept / reject / edit / confirm / export / line-height / open-builder handlers that used to
  // live here belonged to the in-page review workspace. The builder owns all of that now:
  // useCoachSuggestionActions writes accepts straight into the resume store, and the builder's own
  // right rail already has export, typography and layout.

  return (
    <>
      <Helmet>
        <title>
          AI Optimization - Coach LMS
        </title>
      </Helmet>

      {loading && <ResumeScanLoader resumeData={previewData} />}

      <div className="flex min-h-[calc(100vh-2rem)] flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Optimize Your Resume</h1>
          <p className="mt-1 text-sm opacity-70">
            Upload your latest draft and the target JD to align your experience with recruiter
            expectations.
          </p>
        </div>

        {showOptimizationEntry && (
          <div className="grid gap-4 xl:grid-cols-2">
            <FieldShell
              icon={<Briefcase size={22} />}
              title="Target Job"
              subtitle="Paste or upload the job description for target role"
            >
              <textarea
                value={jobDescription}
                onChange={(event) => setJobDescription(event.target.value)}
                placeholder="Paste here"
                className="min-h-[196px] w-full resize-y rounded border border-border bg-background/70 p-3 text-sm outline-none transition-colors placeholder:opacity-50 focus:border-primary"
              />

              <div className="my-3 flex items-center gap-3 text-xs opacity-60">
                <div className="h-px flex-1 bg-border" />
                <span>OR</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <label className="mx-auto flex w-fit cursor-pointer items-center gap-2 rounded border border-primary px-4 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/10">
                <UploadSimple size={16} />
                {jobFile ? jobFile.name : "Upload JD"}
                <Input
                  type="file"
                  accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  className="hidden"
                  onChange={(event) => handleJobFileChange(event.target.files?.[0])}
                />
              </label>

              <p className="mt-3 text-center text-[11px] opacity-60">
                PDF, DOCX, TXT - Max File Size: 10 MB
              </p>
            </FieldShell>

            <FieldShell icon={<FileText size={22} />} title="Your Resume" subtitle="PDF or DOCX">
              <label
                className={cn(
                  "flex min-h-[196px] cursor-pointer flex-col items-center justify-center rounded border border-dashed border-border bg-background/70 p-6 text-center transition-colors hover:border-primary/70 hover:bg-primary/5",
                  resumeFile && "border-primary/70 bg-primary/5",
                )}
              >
                <CloudArrowUp size={28} className="mb-3 opacity-75" />
                <span className="text-sm">
                  {resumeFile ? resumeFile.name : "Drag & Drop your resume"}
                </span>
                <span className="my-2 text-xs opacity-60">OR</span>
                <span className="rounded bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
                  Browse
                </span>
                <Input
                  type="file"
                  accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  className="hidden"
                  onChange={(event) => handleResumeFileChange(event.target.files?.[0])}
                />
              </label>

              <div className="my-3 flex items-center gap-3 text-xs opacity-60">
                <div className="h-px flex-1 bg-border" />
                <span>OR</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <label className="mb-2 block text-xs font-medium">Select Existing Resume</label>
              <Select
                value={selectedResumeId}
                disabled={loadingResumes}
                onValueChange={(value) => {
                  setSelectedResumeId(value);
                  if (value !== "none") setResumeFile(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Resume" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Resume</SelectItem>
                  {resumes?.map((resume) => (
                    <SelectItem key={resume.id} value={resume.id}>
                      {resume.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldShell>
          </div>
        )}

        {/* The AiReviewWorkspace that used to render here is gone: optimizing now navigates straight
            to the builder, which reviews the suggestions in place. */}

        {/* The "Resume Updated Successfully!" modal is gone with the review workspace that used to
            trigger it. Nothing on this page saves a résumé any more, so it had become unreachable —
            the builder's own confirm flow owns that confirmation now. */}

        {showOptimizationEntry && (
          <div className="sticky bottom-0 -mx-6 mt-auto flex justify-end border-t border-border bg-background/95 px-6 py-4 backdrop-blur lg:-mx-8 lg:px-8">
            <Button onClick={onOptimize} disabled={!canOptimize} className="min-w-[112px]">
              <MagicWand className="mr-2" />
              Optimize
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
